import { createTheme } from '@mui/material/styles';

export const getTheme = (mode = 'light') => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isDark ? '#434bbd' : '#2d3484',
        light: isDark ? '#6366f1' : '#4852ab',
        dark: isDark ? '#1e2460' : '#1c215e',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#f15b28',
        light: '#f88258',
        dark: '#c93f10',
        contrastText: '#ffffff',
      },
      tertiary: {
        main: 'rgba(241, 91, 40, 0.12)',
      },
      cuaternary: {
        main: '#6861a5',
        light: '#948ec4',
        dark: '#453f78',
        contrastText: '#ffffff',
      },
      success: {
        main: '#10b981',
        light: '#34d399',
        dark: '#059669',
        contrastText: '#ffffff',
      },
      warning: {
        main: '#f59e0b',
        light: '#fbbf24',
        dark: '#d97706',
      },
      error: {
        main: '#ef4444',
        light: '#f87171',
        dark: '#b91c1c',
      },
      background: {
        default: isDark ? '#0b0f19' : '#f8fafc',
        paper: isDark ? '#131b2e' : '#ffffff',
      },
      text: {
        primary: isDark ? '#f8fafc' : '#0f172a',
        secondary: isDark ? '#94a3b8' : '#64748b',
      },
      divider: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0',
    },
    shape: {
      borderRadius: 10,
    },
    typography: {
      fontFamily: '"Outfit", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 600,
      fontWeightBold: 700,
      fontWeightHeavy: 900,
      h4: {
        fontWeight: 700,
        letterSpacing: '-0.02em',
      },
      h5: {
        fontWeight: 700,
        letterSpacing: '-0.01em',
      },
      h6: {
        fontWeight: 600,
      },
      subtitle1: {
        fontWeight: 600,
      },
      button: {
        textTransform: 'none',
        fontWeight: 700,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? '#0b0f19' : '#f8fafc',
            color: isDark ? '#f8fafc' : '#0f172a',
            transition: 'background-color 0.25s ease, color 0.25s ease',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '10px 20px',
            transition: 'all 0.2s ease',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: isDark ? '0 4px 14px rgba(0, 0, 0, 0.4)' : '0 4px 14px rgba(45, 52, 132, 0.15)',
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          },
          containedPrimary: {
            backgroundColor: isDark ? '#434bbd' : '#2d3484',
            '&:hover': {
              backgroundColor: isDark ? '#373ea8' : '#23296c',
            },
          },
          containedSecondary: {
            backgroundColor: '#f15b28',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#d84e1f',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            border: 'none !important',
            borderBottom: 'none !important',
            backgroundColor: isDark ? '#1e293b' : '#2d3484',
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          '*::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '*::-webkit-scrollbar-track': {
            background: isDark ? '#0f172a' : '#f1f5f9',
          },
          '*::-webkit-scrollbar-thumb': {
            background: isDark ? '#334155' : '#cbd5e1',
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: isDark ? '#475569' : '#94a3b8',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 12,
            backgroundColor: isDark ? '#131b2e' : '#ffffff',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
          },
          elevation1: {
            boxShadow: isDark ? '0 4px 20px rgba(0, 0, 0, 0.4)' : '0 2px 12px rgba(45, 52, 132, 0.04)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            backgroundColor: isDark ? '#131b2e' : '#ffffff',
            backgroundImage: 'none',
            boxShadow: isDark ? '0 25px 50px rgba(0, 0, 0, 0.6)' : '0 20px 50px rgba(0, 0, 0, 0.15)',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#131b2e' : '#ffffff',
            backgroundImage: 'none',
            color: isDark ? '#f8fafc' : '#0f172a',
            borderRight: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: isDark ? '#94a3b8' : '#64748b',
            '&.Mui-focused': {
              color: isDark ? '#818cf8' : '#2d3484',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: isDark ? '#f8fafc' : '#0f172a',
          },
          input: {
            color: isDark ? '#f8fafc' : '#0f172a',
            fontWeight: 600,
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: isDark ? '#94a3b8' : '#64748b',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : '#ffffff',
              transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              '& fieldset': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.15)' : '#e2e8f0',
              },
              '&:hover fieldset': {
                borderColor: isDark ? '#818cf8' : '#2d3484',
              },
              '&.Mui-focused fieldset': {
                borderColor: isDark ? '#818cf8' : '#2d3484',
                borderWidth: '2px',
              },
            },
          },
        },
      },
    },
  });
};

const defaultTheme = getTheme('light');
export default defaultTheme;
